import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const IntegrationSettings = () => {
  const [selectedIntegration, setSelectedIntegration] = useState(null);

  const integrations = [
    {
      id: 'analytics',
      name: 'Google Analytics',
      description: 'Track website traffic and user behavior',
      icon: 'BarChart3',
      status: 'connected',
      category: 'Analytics',
      config: {
        trackingId: 'UA-123456789-1',
        enableEcommerce: true
      }
    },
    {
      id: 'stripe',
      name: 'Stripe',
      description: 'Accept payments and manage subscriptions',
      icon: 'CreditCard',
      status: 'connected',
      category: 'Payment',
      config: {
        publicKey: 'pk_test_51H***************',
        webhookUrl: 'https://api.appcreator.app/webhooks/stripe'
      }
    },
    {
      id: 'sendgrid',
      name: 'SendGrid',
      description: 'Send transactional and marketing emails',
      icon: 'Mail',
      status: 'disconnected',
      category: 'Email',
      config: null
    },
    {
      id: 'cloudinary',
      name: 'Cloudinary',
      description: 'Manage and optimize images and videos',
      icon: 'Image',
      status: 'connected',
      category: 'Media',
      config: {
        cloudName: 'demo-cloud',
        apiKey: '123456789012345'
      }
    },
    {
      id: 'firebase',
      name: 'Firebase',
      description: 'Real-time database and authentication',
      icon: 'Database',
      status: 'disconnected',
      category: 'Backend',
      config: null
    },
    {
      id: 'sentry',
      name: 'Sentry',
      description: 'Error tracking and performance monitoring',
      icon: 'Bug',
      status: 'connected',
      category: 'Monitoring',
      config: {
        dsn: 'https://abc123@sentry.io/123456'
      }
    }
  ];

  const categories = ['All', 'Analytics', 'Payment', 'Email', 'Media', 'Backend', 'Monitoring'];
  const [activeCategory, setActiveCategory] = useState('All');

  const filteredIntegrations = activeCategory === 'All' 
    ? integrations 
    : integrations?.filter(int => int?.category === activeCategory);

  const handleConnect = (integration) => {
    setSelectedIntegration(integration);
  };

  const handleDisconnect = (integrationId) => {
    console.log('Disconnecting:', integrationId);
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h3 className="font-heading font-semibold text-lg md:text-xl text-foreground">
              Integrations
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Connect third-party services to enhance your application
            </p>
          </div>
          <Button variant="default" size="sm" iconName="Plus" iconPosition="left">
            Add Custom
          </Button>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {categories?.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-3 py-1.5 rounded-lg caption font-medium transition-all duration-250 ${
                activeCategory === category
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredIntegrations?.map((integration) => (
            <div
              key={integration?.id}
              className="p-4 bg-background border border-border rounded-lg hover:shadow-sm transition-all duration-250"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name={integration?.icon} size={20} className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="caption font-semibold text-foreground mb-1">
                      {integration?.name}
                    </h4>
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {integration?.description}
                    </p>
                  </div>
                </div>
                <div className={`px-2 py-1 rounded text-xs font-medium flex-shrink-0 ${
                  integration?.status === 'connected' ?'bg-success/10 text-success' :'bg-muted text-muted-foreground'
                }`}>
                  {integration?.status === 'connected' ? 'Connected' : 'Not Connected'}
                </div>
              </div>

              {integration?.status === 'connected' && integration?.config && (
                <div className="mb-3 p-3 bg-muted rounded-lg space-y-2">
                  {Object.entries(integration?.config)?.map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground capitalize">
                        {key?.replace(/([A-Z])/g, ' $1')?.trim()}:
                      </span>
                      <span className="text-xs text-foreground font-mono truncate ml-2 max-w-[200px]">
                        {value?.toString()}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-2">
                {integration?.status === 'connected' ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleConnect(integration)}
                      iconName="Settings"
                      iconPosition="left"
                      className="flex-1"
                    >
                      Configure
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDisconnect(integration?.id)}
                      iconName="Unplug"
                      iconPosition="left"
                      className="flex-1"
                    >
                      Disconnect
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => handleConnect(integration)}
                    iconName="Link"
                    iconPosition="left"
                    fullWidth
                  >
                    Connect
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <h3 className="font-heading font-semibold text-lg md:text-xl text-foreground mb-4 md:mb-6">
          API Endpoints
        </h3>
        
        <div className="space-y-4">
          <div className="p-4 bg-muted rounded-lg">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-3">
              <div>
                <p className="caption font-medium text-foreground">Base URL</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Use this URL for all API requests
                </p>
              </div>
              <Button variant="outline" size="sm" iconName="Copy" iconPosition="left">
                Copy
              </Button>
            </div>
            <code className="block p-3 bg-background border border-border rounded text-xs font-mono text-foreground break-all">
              https://api.appcreator.app/v1/projects/ecommerce-store
            </code>
          </div>

          <div className="p-4 bg-muted rounded-lg">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-3">
              <div>
                <p className="caption font-medium text-foreground">API Key</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Keep this key secure and never share it publicly
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" iconName="Eye" iconPosition="left">
                  Show
                </Button>
                <Button variant="outline" size="sm" iconName="RefreshCw" iconPosition="left">
                  Regenerate
                </Button>
              </div>
            </div>
            <code className="block p-3 bg-background border border-border rounded text-xs font-mono text-foreground">
              ••••••••••••••••••••••••••••••••
            </code>
          </div>

          <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <Icon name="AlertTriangle" size={20} className="text-warning mt-0.5 flex-shrink-0" />
              <div>
                <p className="caption font-medium text-warning">Security Notice</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Never expose your API key in client-side code. Use environment variables and server-side requests for sensitive operations.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <h3 className="font-heading font-semibold text-lg md:text-xl text-foreground mb-4 md:mb-6">
          Webhooks
        </h3>
        
        <div className="space-y-4">
          <Input
            label="Webhook URL"
            type="url"
            placeholder="https://your-domain.com/webhooks"
            description="Receive real-time notifications about events in your application"
          />

          <div className="space-y-2">
            <label className="caption font-medium text-foreground">Events to Subscribe</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {['user.created', 'user.updated', 'order.created', 'payment.succeeded']?.map((event) => (
                <label key={event} className="flex items-center gap-2 p-3 bg-muted rounded-lg cursor-pointer hover:bg-muted/80 transition-all">
                  <input type="checkbox" className="w-4 h-4 text-primary" />
                  <span className="text-sm text-foreground font-mono">{event}</span>
                </label>
              ))}
            </div>
          </div>

          <Button variant="default" size="sm" iconName="Plus" iconPosition="left">
            Add Webhook
          </Button>
        </div>
      </div>
    </div>
  );
};

export default IntegrationSettings;